import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import {
    Moon,
    Sun,
    PanelLeftClose,
    PanelLeftOpen,
    Menu,
    X,
} from "lucide-react";

import {
    LayoutDashboard,
    Users,
    UserCog,
    UserCircle,
    Wrench,
    Shield,
    ClipboardList,
    Settings,
    Tag,
    LogOut,
    ChevronDown,
    ChevronRight,
    LayoutTemplate,
} from "lucide-react";

import logo from "../../assets/fundiLogo.svg";
import PageTransition from "../transition/PageTransition";
import { hasPermission } from "../../utils/uiAccessControl";

const getInitialTheme = () => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored;

    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
};

const getInitialSidebarCollapsed = () => {
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored === "true";
};

function SidebarSectionLabel({ children, collapsed = false }) {
    if (collapsed) return <div className="h-2" />;

    return (
        <div className="px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
            {children}
        </div>
    );
}

function SidebarItem({ to, icon: Icon, label, badge, collapsed = false }) {
    const baseLink =
        "group relative flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm transition-all duration-200";

    const activeLink =
        "bg-slate-900 text-white shadow-sm dark:bg-white dark:text-black";

    const inactiveLink =
        "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white";

    return (
        <NavLink
            to={to}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
                `${baseLink} ${isActive ? activeLink : inactiveLink} ${collapsed ? "justify-center px-3" : ""
                }`
            }
        >
            {({ isActive }) => (
                <>
                    <span
                        className={`flex min-w-0 items-center ${collapsed ? "justify-center" : "gap-3"
                            }`}
                    >
                        <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition ${isActive
                                ? "bg-white/15 dark:bg-black/10"
                                : "bg-slate-100 dark:bg-slate-800"
                                }`}
                        >
                            <Icon
                                className={`h-4 w-4 ${isActive
                                    ? "text-white dark:text-black"
                                    : "text-slate-500 dark:text-slate-400"
                                    }`}
                            />
                        </span>

                        {!collapsed && <span className="truncate font-medium">{label}</span>}
                    </span>

                    {!collapsed && badge ? (
                        <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${isActive
                                ? "bg-white/15 text-white dark:bg-black/10 dark:text-black"
                                : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                }`}
                        >
                            {badge}
                        </span>
                    ) : null}
                </>
            )}
        </NavLink>
    );
}

function SidebarTreeButton({
    icon: Icon,
    label,
    open,
    onClick,
    collapsed = false,
}) {
    const navButtonBase =
        "group flex w-full items-center justify-between rounded-2xl px-4 py-3 text-sm transition-all duration-200";
    const navButtonActive =
        "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white";
    const navButtonInactive =
        "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white";

    return (
        <button
            onClick={onClick}
            title={collapsed ? label : undefined}
            className={`${navButtonBase} ${open ? navButtonActive : navButtonInactive
                } ${collapsed ? "justify-center px-3" : ""}`}
        >
            <span
                className={`flex min-w-0 items-center ${collapsed ? "justify-center" : "gap-3"
                    }`}
            >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
                    <Icon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                </span>
                {!collapsed && <span className="font-medium">{label}</span>}
            </span>

            {!collapsed && (
                <ChevronDown
                    className={`h-4 w-4 text-slate-400 transition-transform dark:text-slate-500 ${open ? "rotate-180" : ""
                        }`}
                />
            )}
        </button>
    );
}

function Breadcrumbs({ pathname }) {
    const segments = pathname.split("/").filter(Boolean);

    const labelMap = {
        dashboard: "Dashboard",
        users: "Users",
        admins: "Admins",
        technicians: "Technicians",
        customers: "Customers",
        roles: "Roles",
        taskTemplates: "Task Templates",
        tasks: "Tasks",
        settings: "Settings",
        brands: "Brands & Models",
    };

    const crumbs = segments.map((segment, index) => {
        const isNumeric = !Number.isNaN(Number(segment));
        return {
            label: isNumeric
                ? `#${segment}`
                : labelMap[segment] ||
                segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            key: `${segment}-${index}`,
        };
    });

    if (crumbs.length === 0) {
        return (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <span>Home</span>
            </div>
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
            <span>Home</span>
            {crumbs.map((crumb) => (
                <span key={crumb.key} className="inline-flex items-center gap-1">
                    <ChevronRight className="h-3.5 w-3.5" />
                    <span>{crumb.label}</span>
                </span>
            ))}
        </div>
    );
}

function SidebarContent({
    collapsed,
    usersOpen,
    settingsOpen,
    setUsersOpen,
    setSettingsOpen,
    handleLogout,
    theme,
    toggleTheme,
    userName,
    userRole,
}) {
    const subLinkBase =
        "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200";
    const subLinkActive =
        "bg-slate-900 text-white dark:bg-white dark:text-black";
    const subLinkInactive =
        "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white";

    return (
        <>
            <div className="border-b border-slate-200 px-5 py-5 dark:border-slate-800">
                <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <img src={logo} alt="AgriFundi" className="h-7 w-7" />
                    </div>

                    {!collapsed && (
                        <div className="min-w-0">
                            <h1 className="truncate text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                                AgriFundi
                            </h1>
                        </div>
                    )}
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-5">
                <div className="space-y-5">
                    <div className="space-y-2">
                        <SidebarSectionLabel collapsed={collapsed}>Navigation</SidebarSectionLabel>

                        <div className="space-y-1.5">
                            {hasPermission("DASHBOARD_VIEW") && (
                                <SidebarItem
                                    to="/dashboard"
                                    icon={LayoutDashboard}
                                    label="Dashboard"
                                    collapsed={collapsed}
                                />
                            )}

                            {hasPermission("ADMIN_VIEW") && (
                                <>
                                    <SidebarTreeButton
                                        icon={Users}
                                        label="Users"
                                        open={usersOpen}
                                        onClick={() => setUsersOpen((prev) => !prev)}
                                        collapsed={collapsed}
                                    />

                                    {usersOpen && !collapsed && (
                                        <div className="ml-5 border-l border-slate-200 pl-4 dark:border-slate-800">
                                            <div className="space-y-1.5">
                                                {hasPermission("ADMIN_VIEW") && (
                                                    <NavLink
                                                        to="/users/admins"
                                                        className={({ isActive }) =>
                                                            `${subLinkBase} ${isActive ? subLinkActive : subLinkInactive}`
                                                        }
                                                    >
                                                        {({ isActive }) => (
                                                            <>
                                                                <UserCog
                                                                    className={`h-4 w-4 ${isActive
                                                                        ? "text-white dark:text-black"
                                                                        : "text-slate-500 dark:text-slate-400"
                                                                        }`}
                                                                />
                                                                <span className="font-medium">Admins</span>
                                                            </>
                                                        )}
                                                    </NavLink>
                                                )}

                                                {hasPermission("TECHNICIAN_VIEW") && (
                                                    <NavLink
                                                        to="/users/technicians"
                                                        className={({ isActive }) =>
                                                            `${subLinkBase} ${isActive ? subLinkActive : subLinkInactive}`
                                                        }
                                                    >
                                                        {({ isActive }) => (
                                                            <>
                                                                <Wrench
                                                                    className={`h-4 w-4 ${isActive
                                                                        ? "text-white dark:text-black"
                                                                        : "text-slate-500 dark:text-slate-400"
                                                                        }`}
                                                                />
                                                                <span className="font-medium">Technicians</span>
                                                            </>
                                                        )}
                                                    </NavLink>
                                                )}

                                                {hasPermission("CUSTOMER_VIEW") && (
                                                    <NavLink
                                                        to="/users/customers"
                                                        className={({ isActive }) =>
                                                            `${subLinkBase} ${isActive ? subLinkActive : subLinkInactive}`
                                                        }
                                                    >
                                                        {({ isActive }) => (
                                                            <>
                                                                <UserCircle
                                                                    className={`h-4 w-4 ${isActive
                                                                        ? "text-white dark:text-black"
                                                                        : "text-slate-500 dark:text-slate-400"
                                                                        }`}
                                                                />
                                                                <span className="font-medium">Customers</span>
                                                            </>
                                                        )}
                                                    </NavLink>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {hasPermission("ROLE_VIEW") && (
                                <SidebarItem
                                    to="/roles"
                                    icon={Shield}
                                    label="Roles"
                                    collapsed={collapsed}
                                />
                            )}

                            {hasPermission("TASK_VIEW") && (
                                <SidebarItem
                                    to="/tasks"
                                    icon={ClipboardList}
                                    label="Tasks"
                                    collapsed={collapsed}
                                />
                            )}

                            {hasPermission("TASK_VIEW") && (
                                <SidebarItem
                                    to="/task-templates"
                                    icon={LayoutTemplate}
                                    label="Task Templates"
                                    collapsed={collapsed}
                                />
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <SidebarSectionLabel collapsed={collapsed}>Configuration</SidebarSectionLabel>

                        <div className="space-y-1.5">
                            <SidebarTreeButton
                                icon={Settings}
                                label="Settings"
                                open={settingsOpen}
                                onClick={() => setSettingsOpen((prev) => !prev)}
                                collapsed={collapsed}
                            />

                            {settingsOpen && !collapsed && (
                                <div className="ml-5 border-l border-slate-200 pl-4 dark:border-slate-800">
                                    <NavLink
                                        to="/settings/brands"
                                        className={({ isActive }) =>
                                            `${subLinkBase} ${isActive ? subLinkActive : subLinkInactive}`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <Tag
                                                    className={`h-4 w-4 ${isActive
                                                        ? "text-white dark:text-black"
                                                        : "text-slate-500 dark:text-slate-400"
                                                        }`}
                                                />
                                                <span className="font-medium">Brands & Models</span>
                                            </>
                                        )}
                                    </NavLink>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            <div className="border-t border-slate-200 px-4 py-4 dark:border-slate-800">
                <div className="space-y-2">
                    <button
                        onClick={toggleTheme}
                        title={collapsed ? "Appearance" : undefined}
                        className={`flex w-full items-center ${collapsed ? "justify-center px-3" : "justify-between"
                            } rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800`}
                    >
                        <span className={`flex items-center ${collapsed ? "" : "gap-3"}`}>
                            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white shadow-sm dark:bg-slate-800">
                                {theme === "dark" ? (
                                    <Moon className="h-4 w-4" />
                                ) : (
                                    <Sun className="h-4 w-4" />
                                )}
                            </span>
                            {!collapsed && "Appearance"}
                        </span>

                        {!collapsed && (
                            <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                {theme}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={handleLogout}
                        title={collapsed ? "Logout" : undefined}
                        className={`flex w-full items-center ${collapsed ? "justify-center px-3" : "gap-3"
                            } rounded-2xl px-4 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30`}
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 dark:bg-red-500/10">
                            <LogOut className="h-4 w-4" />
                        </span>
                        {!collapsed && "Logout"}
                    </button>
                </div>
            </div>
        </>
    );
}

export default function MainLayout() {
    const location = useLocation();

    const [usersOpen, setUsersOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [theme, setTheme] = useState(getInitialTheme);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        getInitialSidebarCollapsed
    );
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    useEffect(() => {
        setUsersOpen(location.pathname.startsWith("/users"));
        setSettingsOpen(location.pathname.startsWith("/settings"));
        setMobileSidebarOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle("dark", theme === "dark");
        localStorage.setItem("theme", theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem("sidebar-collapsed", String(sidebarCollapsed));
    }, [sidebarCollapsed]);

    const toggleTheme = () => {
        setTheme((prev) => (prev === "dark" ? "light" : "dark"));
    };

    const handleLogout = () => {
        localStorage.removeItem("user");
        window.location.href = "/login";
    };

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userName =
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "User";
    const userRole = user?.userType || "ADMIN";

    const contentTitle = useMemo(() => {
        if (location.pathname.startsWith("/dashboard")) return "Dashboard";
        if (location.pathname.startsWith("/users/admins")) return "Admins";
        if (location.pathname.startsWith("/users/technicians")) return "Technicians";
        if (location.pathname.startsWith("/users/customers")) return "Customers";
        if (location.pathname.startsWith("/roles")) return "Roles";
        if (location.pathname.startsWith("/tasks")) return "Tasks";
        if (location.pathname.startsWith("/task-templates")) return "Task Templates";
        if (location.pathname.startsWith("/settings/brands")) return "Brands & Models";
        return "Workspace";
    }, [location.pathname]);

    return (
        <div className="flex h-screen bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
            {/* Desktop sidebar */}
            <aside
                className={`hidden shrink-0 border-r border-slate-200 bg-white/90 backdrop-blur-xl transition-all duration-300 xl:flex xl:flex-col dark:border-slate-800 dark:bg-slate-950/95 ${sidebarCollapsed ? "w-[88px]" : "w-[250px]"
                    }`}
            >
                <SidebarContent
                    collapsed={sidebarCollapsed}
                    usersOpen={usersOpen}
                    settingsOpen={settingsOpen}
                    setUsersOpen={setUsersOpen}
                    setSettingsOpen={setSettingsOpen}
                    handleLogout={handleLogout}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    userName={userName}
                    userRole={userRole}
                />
            </aside>

            {/* Mobile drawer */}
            {mobileSidebarOpen && (
                <div className="fixed inset-0 z-50 xl:hidden">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setMobileSidebarOpen(false)}
                    />

                    <div className="absolute inset-y-0 left-0 flex w-[320px] max-w-[85vw] flex-col border-r border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
                        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 dark:border-slate-800">
                            <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                                    <img src={logo} alt="AgriFundi" className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                                        Menu
                                    </p>
                                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                        AgriFundi
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={() => setMobileSidebarOpen(false)}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <SidebarContent
                            collapsed={false}
                            usersOpen={usersOpen}
                            settingsOpen={settingsOpen}
                            setUsersOpen={setUsersOpen}
                            setSettingsOpen={setSettingsOpen}
                            handleLogout={handleLogout}
                            theme={theme}
                            toggleTheme={toggleTheme}
                            userName={userName}
                            userRole={userRole}
                        />
                    </div>
                </div>
            )}

            {/* CONTENT */}
            <main className="flex min-w-0 flex-1 flex-col bg-slate-100 dark:bg-slate-950">
                {/* Top bar */}
                <div className="border-b border-slate-200 bg-white/85 px-4 py-4 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/85 md:px-5">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-start gap-3">
                            {/* mobile menu */}
                            <button
                                onClick={() => setMobileSidebarOpen(true)}
                                className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 xl:hidden dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                                <Menu className="h-4 w-4" />
                            </button>

                            {/* desktop collapse */}
                            <button
                                onClick={() => setSidebarCollapsed((prev) => !prev)}
                                className="mt-0.5 hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 xl:flex dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                                {sidebarCollapsed ? (
                                    <PanelLeftOpen className="h-4 w-4" />
                                ) : (
                                    <PanelLeftClose className="h-4 w-4" />
                                )}
                            </button>

                            <div className="min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                                    Current Module
                                </p>
                                <h2 className="mt-1 truncate text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                                    {contentTitle}
                                </h2>
                                <div className="mt-2">
                                    <Breadcrumbs pathname={location.pathname} />
                                </div>
                            </div>
                        </div>

                        <div className="hidden items-center gap-3 md:flex">
                            {/* Theme toggle */}
                            <button
                                onClick={toggleTheme}
                                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                                title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                            >
                                {theme === "dark" ? (
                                    <Moon className="h-4 w-4" />
                                ) : (
                                    <Sun className="h-4 w-4" />
                                )}
                            </button>

                            {/* User panel */}
                            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                    {userName?.charAt(0)?.toUpperCase() || "U"}
                                </div>

                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                                        {userName}
                                    </p>
                                    <p className="text-xs uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                                        {userRole}
                                    </p>
                                </div>

                                <button
                                    onClick={handleLogout}
                                    className="ml-2 inline-flex items-center justify-center rounded-xl p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                                    title="Logout"
                                >
                                    <LogOut className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page surface */}
                <div className="min-h-0 flex-1 overflow-auto p-4 md:p-5">
                    <div className="min-h-full rounded-[28px] border border-slate-200 bg-white/70 p-4 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/50 dark:shadow-[0_12px_40px_rgba(0,0,0,0.22)] md:p-5">
                        <PageTransition>
                            <Outlet />
                        </PageTransition>
                    </div>
                </div>
            </main>
        </div>
    );
}